import { CollapsibleSection } from "../components/ChildView.jsx";
import { HomeworkReview } from "./HomeworkReview.jsx";
import { HomeworkReceive } from "./HomeworkReceive.jsx";
import { HW_SHARED_TYPE, HW_SUBMISSION_TYPE, isHomeworkRequest } from "../lib/homework.js";
import { getActiveAdultId, getDeviceOwnerAdultId, getHouseholdCode } from "../lib/api.js";
import { actingAdultHere, stampActor } from "../lib/adults.js";
import { NotificationCoverageBanner } from "../components/Onboarding.jsx";
import { ACTIVITY_KIND_STYLE, ActivityList, ActivityRow, CoinIcon, Icon, ImageLightbox, VaultCoinSparkAnimation, VaultIcon } from "../components/Common.jsx";
import { nowIso } from "../lib/constants.js";
import { fmtActivityStamp, fmtPence, fmtTimestamp, redeemSourceOptions, resolveRedeemSplit, todayStr, tokensToPence, weekStartOf } from "../lib/utils.js";
import { t as ct } from "../lib/content.js";
import { imageSrc } from "../lib/origin.js";
import { useBackLayer } from "../lib/backstack.js";
import { ageBand, track } from "../lib/metrics.js";
import { ASK_TO_BUY_TYPE, askAvailability, askImpact, gapSplit } from "../lib/askToBuy.js";
import { uid } from "../lib/constants.js";
const { useState, useEffect, useCallback, useMemo, useRef, useId } = React;

// LB-343 — who approved or rejected this. A request is CREATED by a child, so adultId on a
// request row unambiguously means the adult who resolved it; G2 draws exactly that.
const actor = (s) => actingAdultHere(s, getActiveAdultId, getDeviceOwnerAdultId, getHouseholdCode());

export function RequestsTab({ state, update, showToast, onApproveHomework, onRejectHomework, addTransaction, moveTokensToVault, reverseTransaction, reverseRequest, rejectVaultTransfer, code, onOpenNotificationSettings, onAdjustCap, joinRequests = [], joinBusy = null, onActOnJoinRequest, focusRequest = null, onFocusHandled = () => {} }) {
  const pending = state.requests.filter((r) => r.status === "pending");
  const resolved = state.requests.filter((r) => r.status !== "pending");
  const [pendingPage, setPendingPage] = useState(0);
  // Each token<->vault transfer logs a linked pair of rows (see moveTokensToVault) tagged with
  // vaultTransfer + the exact tokens/pence moved — only take the "vault" side of each pair so
  // every transfer shows once here, not twice.
  const vaultTxs = state.transactions.filter((t) => t.vaultTransfer && t.balance === "vault");
  // Drives the post-approval "real money needs to move too" sequence: a brief animation in a
  // focus window, then a screen reminding the parent to move real money, since the app's vault
  // balance is a user-entered figure with no link to an actual bank account. `direction`
  // controls which way the copy points — "out" for a redemption spending down the vault,
  // "in" for a confirmed transfer that just added to it.
  // null | { phase: "anim" | "reminder", childName, pence, direction: "in" | "out" }
  const [vaultFlow, setVaultFlow] = useState(null);
  const PAGE_SIZE = 15;
  const rate = state.settings?.exchangeRate > 0 ? state.settings.exchangeRate : 10;
  // A parent's explicit override for a given goal_redeem request's funding source, keyed by
  // request id. Three meaningful values (see computeRedeemSplit):
  //  - "token" / "vault" — a full either/or pick, only consulted when both balances alone
  //    could cover the whole amount.
  //  - "vault-only" — the parent has deselected tokens from an otherwise-mixed default split,
  //    only consulted when the Vault alone can cover the whole amount even though tokens
  //    alone can't. Clicking Tokens again clears this back to the default mixed split.
  // Anything else (including no entry) falls through to the balances-driven default.
  const [payFromOverride, setPayFromOverride] = useState({});
  // LB-17: on an ask the child cannot cover, the parent's pick for the gap — "gift" | "loan" —
  // keyed by request id. Cover and approve stays disabled until one is picked.
  const [gapCoverChoice, setGapCoverChoice] = useState({});
  // LB-17 follow-up (Alan, 28 Sep): Cover and approve asks once more before a gift or a loan
  // moves anything — the request id waiting on that confirmation, or null.
  const [gapConfirmId, setGapConfirmId] = useState(null);
  // Shared across every pending-request card — only one photo can be enlarged at a time.
  const [enlargedImage, setEnlargedImage] = useState(null);
  // LB-381: the sheet a child sent, opened for review. A homework sheet cannot be decided from
  // the card — it needs its words read, a date, and sometimes a list archived to make room —
  // so the card opens this and the buttons live in there.
  const [reviewing, setReviewing] = useState(null);
  // LB-315: the review, the gap confirm and the vault walkthrough are
  // layers over the tab. The walkthrough's back is its Done: the approval already happened.
  useBackLayer(!!reviewing, () => setReviewing(null));
  useBackLayer(!!gapConfirmId, () => setGapConfirmId(null));
  useBackLayer(!!vaultFlow, () => setVaultFlow(null));
  function childName(id) { return state.profiles.find((p) => p.id === id)?.name ?? "Unknown"; }
  // The one input App has into this tab (2 Oct, the waiting-requests overlay): "show this
  // request". A child's photographed sheet opens its review, as Check the words does; anything
  // else is scrolled into view and tinted for a moment. Nothing is approved or refused here.
  // `focusRequest` is { id, n }: a fresh object per ask, so the same request can be asked twice.
  const [highlightId, setHighlightId] = useState(null);
  useEffect(() => {
    if (!focusRequest || !focusRequest.id) return;
    const req = state.requests.find((r) => r.id === focusRequest.id && r.status === "pending");
    onFocusHandled();
    if (!req) return;
    if (isHomeworkRequest(req)) { setReviewing(req.id); return; }
    const at = pending.findIndex((r) => r.id === req.id);
    if (at >= 0) setPendingPage(Math.floor(at / PAGE_SIZE));
    setHighlightId(req.id);
    const t1 = setTimeout(() => {
      try { const el = document.querySelector(`[data-request-id="${req.id}"]`); if (el && el.scrollIntoView) el.scrollIntoView({ behavior: "smooth", block: "center" }); } catch {}
    }, 30);
    const t2 = setTimeout(() => setHighlightId((h) => (h === req.id ? null : h)), 2200);
    return () => { clearTimeout(t1); clearTimeout(t2); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusRequest]);
  // Thin wrappers around the shared redeemSourceOptions/resolveRedeemSplit helpers (see their
  // definitions near goalProgress) — those take a raw child + target, these adapt a request.
  // Kept centralized so the toggle rule and the mixed-split math can never drift between this
  // approval flow and GoalsSection's own direct-redeem UI (the single parent-side goals
  // surface as of the R-B addendum's Resolution 2 — ProfilesTab's own copy was removed).
  function requestRedeemSourceOptions(req) {
    const child = state.profiles.find((p) => p.id === req.childId);
    return redeemSourceOptions(child, req.requestedAmount, rate);
  }
  function computeRedeemSplit(req) {
    const child = state.profiles.find((p) => p.id === req.childId);
    if (!child) return { tokenPart: 0, vaultPart: 0 };
    const override = payFromOverride[req.id];
    const chosen = override === "vault" ? "vault" : override === "vault-only" ? "vault-only" : "token";
    return resolveRedeemSplit(child, req.requestedAmount, rate, chosen);
  }
  // LB-17: the numbers the ask card quotes — the same lib/askToBuy.js arithmetic the child saw.
  function askFacts(req) {
    const child = state.profiles.find((p) => p.id === req.childId);
    if (!child) return null;
    const availability = askAvailability(child, req.requestedAmount, rate);
    const split = availability.affordable ? computeRedeemSplit(req) : gapSplit(child, req.requestedAmount, rate);
    const impact = availability.affordable ? askImpact(child, req.requestedAmount, rate, split) : null;
    return { child, availability, split, impact };
  }
  function requestTypeMeta(type) {
    switch (type) {
      case "goal_redeem": return { icon: <Icon id="ic-goals" size={14} hue="indigo" />, border: "border-l-violet-400" };
      case ASK_TO_BUY_TYPE: return { icon: <Icon id="ic-wish" size={14} hue="gold" />, border: "border-l-amber-400" };
      case "habit_reward": return { icon: <Icon id="ic-flame" size={14} hue="amber" />, border: "border-l-amber-400" };
      case "vault_transfer": return { icon: <VaultIcon className="w-3.5 h-3.5 inline-block" />, border: "border-l-teal-400" };
      case "experience_redeem": return { icon: <Icon id="ic-gift" size={14} hue="pink" />, border: "border-l-pink-400" };
      case "learn_earn_reward": return { icon: <Icon id="ic-brain" size={14} hue="indigo" />, border: "border-l-purple-400" };
      case HW_SUBMISSION_TYPE: return { icon: <Icon id="ic-photo" size={14} hue="indigo" />, border: "border-l-indigo-400" };
      case HW_SHARED_TYPE: return { icon: <Icon id="ic-sent" size={14} hue="indigo" />, border: "border-l-indigo-400" };
      default: return { icon: <CoinIcon className="w-3.5 h-3.5 inline-block" />, border: "border-l-emerald-400" };
    }
  }

  const pendingTotalPages = Math.max(1, Math.ceil(pending.length / PAGE_SIZE));
  const pendingPageClamped = Math.min(pendingPage, pendingTotalPages - 1);
  const pendingPageItems = pending.slice(pendingPageClamped * PAGE_SIZE, pendingPageClamped * PAGE_SIZE + PAGE_SIZE);

  // resolved & vaultTxs pagination is now owned by ActivityList itself (see the two
  // CollapsibleSections below) — pending stays hand-rolled since its cards are too tall/varied
  // (Pay-from toggles, multi-line titles) to fit the fixed-row ActivityRow shape.

  function approve(req) {
    // W4 (concurrency Addendum 1): approval must be idempotent. This entry guard kills
    // the double-tap / stale-row class (the req object comes from the rendered state);
    // the per-updater guards below kill the rebase-replay class — an updater replayed
    // onto a server copy where another device already resolved this request finds it
    // non-pending and declares "nothing changed" (update(null) => no write at all).
    if (req.status !== "pending") return;
    // LB-382 receive fixes: a homework request is never approved here. Its list is made in the
    // review (approveHomeworkSheet); the generic branch below would resolve it with nothing made.
    if (isHomeworkRequest(req)) { setReviewing(req.id); return; }
    // W2.5: approvals are where a request becomes a real outcome, so the outcome events
    // live here (the corresponding *_requested moments are tracked where kids send them).
    const reqBand = ageBand(state.profiles.find((p) => p.id === req.childId)) || undefined;
    if (req.requestType === "goal_redeem") track("goal_redeemed", { band: reqBand, value: req.requestedAmount });
    else if (req.requestType === "vault_transfer") track("vault_transfer_approved", { band: reqBand, value: req.requestedAmount });
    else if (req.requestType === "habit_reward") track("habit_reward_approved", { band: reqBand, value: req.requestedAmount });
    if (req.requestType === "goal_redeem") {
      const child = state.profiles.find((p) => p.id === req.childId);
      const { tokenPart, vaultPart } = computeRedeemSplit(req);
      const vaultPence = tokensToPence(vaultPart, rate);
      if (tokenPart > 0) addTransaction(req.childId, tokenPart, req.title, "spend", "token", req.id);
      if (vaultPart > 0) {
        addTransaction(req.childId, vaultPence, req.title, "spend", "vault", req.id);
        // Any redemption drawing on the Savings Vault — mixed or vault-only — is real money
        // leaving it just like a transfer would, so tag the spend row just logged above as a
        // "from Savings Vault" vault transaction too, alongside its regular appearance in
        // Recently resolved. This is what lets a parent reconcile the vault against a real
        // bank account regardless of whether tokens also contributed.
        update((s) => {
          if (s.transactions[0]) { s.transactions[0].vaultTransfer = "from"; s.transactions[0].tokens = vaultPart; s.transactions[0].pence = vaultPence; }
          return s;
        });
      }
      const source = tokenPart > 0 && vaultPart > 0 ? "mixed" : vaultPart > 0 ? "vault" : "token";
      update((s) => {
        const child = s.profiles.find((p) => p.id === req.childId);
        if (child) child.goals = (child.goals || []).filter((g) => g.id !== req.goalId);
        const r = s.requests.find((x) => x.id === req.id);
        if (!r || r.status !== "pending") return null;
        { r.status = "approved"; r.resolvedAt = nowIso(); r.fundingSource = source; stampActor(r, actor(s)); }
        return s;
      });
      // Any Savings Vault involvement (mixed or vault-only) means real money needs to physically
      // move, which this app can't do — walk the parent through it instead of a silent approve.
      if (vaultPart > 0) {
        setVaultFlow({ phase: "anim", childName: child?.name || "your child", pence: vaultPence });
      }
    } else if (req.requestType === "vault_transfer") {
      // The transfer already happened for real the moment the child sent it — see
      // submitVaultTransfer — so approving here is just flipping the already-written pair
      // of rows from "pending" to "approved", not moving anything again.
      const child = state.profiles.find((p) => p.id === req.childId);
      const pence = req.pence || tokensToPence(req.requestedAmount, rate);
      update((s) => {
        s.transactions.filter((t) => t.requestId === req.id && t.status === "pending").forEach((t) => { t.status = "approved"; });
        const r = s.requests.find((x) => x.id === req.id);
        if (!r || r.status !== "pending") return null;
        { r.status = "approved"; r.resolvedAt = nowIso(); stampActor(r, actor(s)); }
        return s;
      });
      setVaultFlow({ phase: "anim", childName: child?.name || "your child", pence, direction: "in" });
    } else if (req.requestType === ASK_TO_BUY_TYPE) {
      // LB-17. Affordable: the goal-redemption path minus the goal — Pay-from picker, tokens-
      // first split, spend rows tagged to the request, the vault walkthrough if the vault paid.
      // Short: the parent covers the gap (Alan, 28 Sep) as a GIFT (a gift credit for the gap,
      // then the full spend — both rows on the request, the gift row tagged askGift) or a LOAN
      // (the full spend runs as it stands and the token balance goes below zero; the request
      // records the loan and earnings bring the balance back — no separate ledger).
      const facts = askFacts(req);
      if (!facts) return;
      const { child, availability, split } = facts;
      const gapCover = availability.affordable ? null : gapCoverChoice[req.id];
      if (!availability.affordable && gapCover !== "gift" && gapCover !== "loan") return; // Cover and approve needs a pick
      const { tokenPart, vaultPart } = split;
      const vaultPence = vaultPart > 0 ? tokensToPence(vaultPart, rate) : 0;
      if (gapCover === "gift" && availability.shortfall > 0) {
        addTransaction(req.childId, availability.shortfall, ct("askbuy.gift_reason", "Gift towards {what}", { what: req.title }), "credit", "token", req.id, { addMoney: true, askGift: true });
      }
      if (tokenPart > 0) addTransaction(req.childId, tokenPart, req.title, "spend", "token", req.id, { till: true, ...(gapCover === "loan" ? { askLoan: availability.shortfall } : {}) });
      if (vaultPart > 0) {
        addTransaction(req.childId, vaultPence, req.title, "spend", "vault", req.id, { till: true });
        update((s) => {
          if (s.transactions[0]) { s.transactions[0].vaultTransfer = "from"; s.transactions[0].tokens = vaultPart; s.transactions[0].pence = vaultPence; }
          return s;
        });
      }
      const source = tokenPart > 0 && vaultPart > 0 ? "mixed" : vaultPart > 0 ? "vault" : "token";
      update((s) => {
        const r = s.requests.find((x) => x.id === req.id);
        if (!r || r.status !== "pending") return null;
        r.status = "approved"; r.resolvedAt = nowIso(); r.fundingSource = source;
        if (gapCover) { r.gapCover = gapCover; r.gapTokens = availability.shortfall; }
        stampActor(r, actor(s));
        return s;
      });
      track("ask_to_buy_approved", { band: reqBand, value: req.pricePence || tokensToPence(req.requestedAmount, rate), kind: gapCover || "own" });
      if (vaultPart > 0) setVaultFlow({ phase: "anim", childName: child?.name || "your child", pence: vaultPence });
      setGapCoverChoice((o) => { if (!(req.id in o)) return o; const next = { ...o }; delete next[req.id]; return next; });
    } else if (req.requestType === "experience_redeem") {
      addTransaction(req.childId, req.requestedAmount, req.title, "spend", "token", req.id);
      update((s) => { const r = s.requests.find((x) => x.id === req.id); if (!r || r.status !== "pending") return null; { r.status = "approved"; r.resolvedAt = nowIso(); stampActor(r, actor(s)); } return s; });
    } else if (req.requestType === "learn_earn_reward") {
      // A Learn & Earn reward that breached the child's spending cap when they finished the
      // test (see App.jsx's onLearnEarn) — approving it here is the delayed version of the
      // instant credit every other Learn & Earn pass gets, so it needs to replicate that same
      // per-game weekly payout bookkeeping (and the spelling retest reset) that onLearnEarn
      // would otherwise have done at the moment the child finished.
      addTransaction(req.childId, req.requestedAmount, req.title, "credit", "token", req.id, { earnCap: true, learnEarnKind: req.learnEarnKind });
      update((s) => {
        const r = s.requests.find((x) => x.id === req.id); if (!r || r.status !== "pending") return null; { r.status = "approved"; r.resolvedAt = nowIso(); stampActor(r, actor(s)); }
        const lc = s.learningContent[req.childId];
        if (lc && req.learnEarnKind) {
          const wk = weekStartOf(todayStr());
          const weekField = `${req.learnEarnKind}PayoutWeek`;
          const countField = `${req.learnEarnKind}PayoutCount`;
          const tiersField = `${req.learnEarnKind}PayoutTiers`;
          const fresh = lc[weekField] !== wk;
          lc[countField] = fresh ? 1 : (lc[countField] || 0) + 1;
          lc[tiersField] = [...(fresh ? [] : lc[tiersField] || []), req.learnEarnTier].filter(Boolean);
          lc[weekField] = wk;
          if (req.learnEarnKind === "spelling") {
            (lc.spellingWords || []).forEach((w) => { if (w.testInclude !== false) w.testInclude = false; });
          }
        }
        return s;
      });
    } else {
      // Covers "earn" (ad-hoc asks + earning-activity pills) and "habit_reward" — both are
      // real new earnings, so they're tagged earnCap so they count toward the child's weekly
      // spending-cap progress (see earnedThisWeekPence).
      addTransaction(req.childId, req.requestedAmount, req.title, "credit", "token", req.id, { earnCap: true });
      update((s) => { const r = s.requests.find((x) => x.id === req.id); if (!r || r.status !== "pending") return null; { r.status = "approved"; r.resolvedAt = nowIso(); stampActor(r, actor(s)); } return s; });
    }
    setPayFromOverride((o) => { if (!(req.id in o)) return o; const next = { ...o }; delete next[req.id]; return next; });
  }
  function reject(req, silent) {
    if (req.status !== "pending") return; // W4 idempotency — see approve()
    if (isHomeworkRequest(req)) return; // LB-382: declined in its review, never from the row
    if (req.requestType === "vault_transfer") {
      rejectVaultTransfer(req.id, "rejected");
    } else {
      update((s) => { const r = s.requests.find((x) => x.id === req.id); if (!r || r.status !== "pending") return null; { r.status = "rejected"; r.resolvedAt = nowIso(); stampActor(r, actor(s)); } return s; });
    }
    if (req.requestType === ASK_TO_BUY_TYPE) track("ask_to_buy_refused", { band: ageBand(state.profiles.find((p) => p.id === req.childId)) || undefined });
    if (!silent) showToast(req.requestType === ASK_TO_BUY_TYPE ? ct("toast.ask_to_buy_refused", "Not this time. {name} will see that, with no name on it.", { name: childName(req.childId) }) : ct("toast.request_rejected", "Request rejected"), "bad");
    setPayFromOverride((o) => { if (!(req.id in o)) return o; const next = { ...o }; delete next[req.id]; return next; });
  }
  // LB-17: the parent turns the ask into a goal instead — name, price and photo carried over,
  // nothing spent, the request closes as "goal" (not rejected: the child is told to keep saving).
  function makeGoal(req) {
    if (req.status !== "pending") return;
    update((s) => {
      const r = s.requests.find((x) => x.id === req.id);
      if (!r || r.status !== "pending") return null;
      const child = s.profiles.find((p) => p.id === req.childId);
      if (child) child.goals = [...(child.goals || []), { id: uid(), title: req.title, targetPence: req.pricePence || tokensToPence(req.requestedAmount, rate), image: req.image || null }];
      r.status = "goal"; r.resolvedAt = nowIso(); stampActor(r, actor(s));
      return s;
    });
    track("ask_to_buy_goal", { band: ageBand(state.profiles.find((p) => p.id === req.childId)) || undefined, kind: "parent" });
    showToast(ct("toast.ask_to_buy_goal", "Added to {name}\u2019s goals", { name: childName(req.childId) }), "good");
    setGapCoverChoice((o) => { if (!(req.id in o)) return o; const next = { ...o }; delete next[req.id]; return next; });
  }
  function approveAll() {
    // LB-381: a homework sheet is never approved in bulk. It needs a practise-until date and,
    // when the child's grid is full, a list picked to come off — neither of which a blanket
    // Approve all can answer, and silently guessing both would be worse than leaving it.
    // LB-17: an ask the child cannot cover needs the parent's gift-or-loan pick, so it is
    // never approved in bulk either.
    const bulk = pending.filter((r) => !isHomeworkRequest(r) && !(r.requestType === ASK_TO_BUY_TYPE && !(askFacts(r)?.availability.affordable)));
    const count = bulk.length;
    if (count === 0) return;
    bulk.forEach((r) => approve(r));
    showToast(ct("toast.requests_approved", "Approved {count} request{plural}", { count, plural: count === 1 ? "" : "s" }), "good");
  }
  function rejectAll() {
    const bulk = pending.filter((r) => !isHomeworkRequest(r));
    const count = bulk.length;
    if (count === 0) return;
    bulk.forEach((r) => reject(r, true));
    showToast(ct("toast.requests_rejected_count", "Rejected {count} request{plural}", { count, plural: count === 1 ? "" : "s" }), "bad");
  }

  // LB-381: the sheet takes over the tab while it is being read, rather than opening a modal
  // over a list of other people's asks — a parent checking twenty spellings wants the screen.
  const reviewReq = reviewing ? state.requests.find((r) => r.id === reviewing && r.status === "pending") : null;
  if (reviewReq) {
    return (
      <div className="flex flex-col gap-2 lb-form">
        <button onClick={() => setReviewing(null)} className="self-start text-xs font-bold text-slate-900 underline" data-testid="hw-review-back">{ct("hwreview.back", "Back to requests")}</button>
        {reviewReq.requestType === HW_SHARED_TYPE ? (
          /* LB-382: a list another family shared arrives with no child. Which child first, then
             the same review a sheet from the child goes through. "No thanks" declines it; Decide
             later leaves it waiting in Requests. */
          <HomeworkReceive
            state={state}
            shared={{ code: reviewReq.sharedCode, name: reviewReq.title, type: reviewReq.listType, lang: reviewReq.lang, direction: reviewReq.direction, schoolYear: reviewReq.schoolYear, pairs: reviewReq.pairs }}
            onAdd={(childId, meta, until, archiveFirst) => { onApproveHomework(reviewReq, meta.pairs, until, archiveFirst, { childId, name: meta.name, lang: meta.lang, direction: meta.direction, oinksOn: meta.oinksOn, finishTokens: meta.finishTokens }); setReviewing(null); }}
            onDiscard={() => { onRejectHomework(reviewReq, ""); setReviewing(null); }}
            onClose={() => setReviewing(null)}
          />
        ) : (
          <HomeworkReview
            state={state}
            childId={reviewReq.childId}
            req={reviewReq}
            listType={reviewReq.listType || "spelling"}
            lang={reviewReq.lang || ""}
            initialName={reviewReq.title || ""}
            onApprove={(pairs, until, archiveFirst, extra) => { onApproveHomework(reviewReq, pairs, until, archiveFirst, extra); setReviewing(null); }}
            onReject={(reason) => onRejectHomework(reviewReq, reason)}
            onClose={() => setReviewing(null)}
          />
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2 lb-form">
      {/* Sits above the queue rather than in Settings because this is exactly where the
          consequence shows up: a parent looking at a list of things waiting on them is the
          person best placed to notice nobody is being told about them. */}
      <NotificationCoverageBanner code={code} onOpenSettings={onOpenNotificationSettings} />
      {/* J1 — a device asking to join waits here, with everything else waiting on a parent.
          Its own card above the queue rather than a row inside it: the queue is money and
          this is access, and the two should not be approved with the same flick of a thumb.
          The worker decides who sees these at all — the adult who sent the link, or one who
          may manage adults — so an empty list here is an answer, not a permission check. */}
      {joinRequests.length > 0 && onActOnJoinRequest && (
        <div className="bg-white rounded-2xl p-4 shadow-sm border border-amber-200" data-testid="join-requests-card">
          <div className="text-sm font-semibold text-slate-700 mb-3">{ct("join.card_title", "Devices waiting to join")}</div>
          <div className="flex flex-col gap-2">
            {joinRequests.map((r) => (
              <div key={r.requestId} className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <div className="text-sm font-bold text-slate-800">{r.label || ct("join.overlay_unknown_device", "A device")}</div>
                <div className="text-xs text-slate-500 font-medium mb-2">
                  {[r.where, r.mine ? ct("join.row_you", "your link") : ct("join.row_other", "another adult's link")].filter(Boolean).join(" · ")}
                </div>
                <div className="flex gap-2">
                  <button type="button" data-testid="join-row-approve" disabled={joinBusy === r.requestId} className="flex-1 bg-slate-900 text-white font-bold text-xs rounded-lg py-2 disabled:opacity-50" onClick={() => onActOnJoinRequest(r.requestId, "approve")}>{ct("join.row_approve", "Approve")}</button>
                  <button type="button" data-testid="join-row-cancel" disabled={joinBusy === r.requestId} className="flex-1 text-rose-700 font-bold text-xs rounded-lg py-2 border border-rose-200 disabled:opacity-50" onClick={() => onActOnJoinRequest(r.requestId, "cancel")}>{ct("join.row_cancel", "Cancel the link")}</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      <div className="bg-white rounded-2xl p-4 shadow-sm border border-slate-100">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <div className="text-sm font-semibold text-slate-700">Pending approval ({pending.length})</div>
          {pending.length > 1 && (
            <div className="flex gap-2">
              <button className="text-xs font-bold text-navy px-1 py-1" onClick={approveAll}>{ct("requests.approve_all", "Approve all")}</button>
              <button className="text-xs font-bold text-rose-700 px-1 py-1" onClick={rejectAll}>{ct("requests.reject_all", "Reject all")}</button>
            </div>
          )}
        </div>
        {pending.length === 0 && <div className="text-base text-slate-400">{ct("requests.nothing_waiting", "Nothing waiting on you right now.")}</div>}
        <div className="flex flex-col gap-2">
          {pendingPageItems.map((r) => {
            const meta = requestTypeMeta(r.requestType);
            return (
            <div key={r.id} data-request-id={r.id} className={`border border-slate-100 rounded-xl p-3 border-l-4 ${meta.border} transition-colors ${highlightId === r.id ? "bg-indigo-50" : ""}`}>
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs font-medium text-slate-500 flex items-center gap-1.5">{meta.icon} {requestWhoLabel(r, childName)}</span>
                <span className="text-xs font-bold text-amber-600">
                  {r.doubleDay && r.baseAmount && !isHomeworkRequest(r) && r.requestType !== "vault_transfer"
                    ? <><span className="line-through text-slate-400 font-semibold mr-1">+{r.baseAmount}</span>{requestAmountLabel(r, rate)} <span className="ml-1 text-[10px] font-extrabold bg-amber-100 text-amber-800 rounded px-1.5 py-0.5 align-middle" data-testid="double-day-chip">{ct("requests.double_day_chip", "Double token day")}</span></>
                    : requestAmountLabel(r, rate)}
                </span>
              </div>
              <div className="text-sm font-semibold text-slate-900 mb-1">{r.title}</div>
              <div className="text-xs text-slate-400 mb-2">{requestWhenLabel(r)}</div>
              {r.requestType === ASK_TO_BUY_TYPE && (() => {
                // LB-17 (P1a/P1b): price in £ and tokens, what the child has, the goal impact,
                // then EITHER the goal-redemption Pay-from picker (affordable) OR the gap
                // question (short). The Pay-from block itself is shared with goal_redeem below.
                const facts = askFacts(r);
                if (!facts) return null;
                const { child, availability, impact } = facts;
                const pricePence = r.pricePence || tokensToPence(r.requestedAmount, rate);
                const gapPence = tokensToPence(availability.shortfall, rate);
                const choice = gapCoverChoice[r.id];
                return (
                  <div className="mb-2 flex flex-col gap-2" data-testid={`ask-card-${r.id}`} data-affordable={availability.affordable ? "true" : "false"}>
                    <div className="text-sm font-bold text-amber-600">{fmtPence(pricePence)} · {r.requestedAmount} tokens</div>
                    <div className="text-xs text-slate-500" data-testid="ask-balance-line">
                      {ct("requests.ask_balance_line", "{name} has {tokens} tokens + {vault} in the vault ({total} tokens)", { name: child.name, tokens: child.tokenBalance, vault: fmtPence(child.vaultBalancePence), total: availability.availableTokens })}
                      {!availability.affordable && <span className="font-bold text-rose-600"> · {ct("requests.ask_short_suffix", "{short} short", { short: availability.shortfall })}</span>}
                    </div>
                    {availability.affordable && impact && impact.after < 100 && impact.before > impact.after && (
                      <div className="bg-amber-50 text-amber-800 text-xs font-semibold rounded-lg px-2.5 py-1.5">{ct("till.impact_line", "\u201C{title}\u201D goes {before}% \u2192 {after}% saved", { title: impact.title, before: impact.before, after: impact.after })}</div>
                    )}
                    {!availability.affordable && (
                      <div className="flex flex-col gap-1.5" data-testid="ask-gap-block">
                        <div className="text-xs font-bold text-slate-500">{ct("requests.ask_gap_question", "Cover the {short}-token gap ({pence})?", { short: availability.shortfall, pence: fmtPence(gapPence) })}</div>
                        <div className="grid grid-cols-2 gap-1.5">
                          <button type="button" data-testid="ask-gap-gift" onClick={() => setGapCoverChoice((o) => ({ ...o, [r.id]: "gift" }))} className={`text-left rounded-lg px-3 py-2 border ${choice === "gift" ? "bg-slate-800 border-slate-800 text-white" : "bg-white border-slate-200 text-slate-900"}`}>
                            <span className="block text-xs font-extrabold">{ct("requests.ask_gap_gift", "As a gift")}</span>
                            <span className={`block text-[11px] ${choice === "gift" ? "text-white/80" : "text-slate-500"}`}>{ct("requests.ask_gap_gift_hint", "{short} tokens credited, then spent. Logged as a gift.", { short: availability.shortfall })}</span>
                          </button>
                          <button type="button" data-testid="ask-gap-loan" onClick={() => setGapCoverChoice((o) => ({ ...o, [r.id]: "loan" }))} className={`text-left rounded-lg px-3 py-2 border ${choice === "loan" ? "bg-slate-800 border-slate-800 text-white" : "bg-white border-slate-200 text-slate-900"}`}>
                            <span className="block text-xs font-extrabold">{ct("requests.ask_gap_loan", "As a loan")}</span>
                            <span className={`block text-[11px] ${choice === "loan" ? "text-white/80" : "text-slate-500"}`}>{ct("requests.ask_gap_loan_hint", "Balance goes to \u2212{short}. Earnings pay it back.", { short: availability.shortfall })}</span>
                          </button>
                        </div>
                        <div className="text-[11px] text-slate-500">{ct("requests.ask_gap_order", "{name}\u2019s {tokens} tokens and {vault} vault go first, tokens before vault.", { name: child.name, tokens: child.tokenBalance, vault: fmtPence(child.vaultBalancePence) })}</div>
                      </div>
                    )}
                  </div>
                );
              })()}
              {isHomeworkRequest(r) && (
                <div className="mb-2" data-testid={`hw-sheet-${r.id}`}>
                  <div className="text-xs text-slate-500 mb-2">{r.requestType === HW_SHARED_TYPE
                    ? [ct("requests.homework_shared_sub", "{n} word{plural}", { n: (r.pairs || []).length, plural: (r.pairs || []).length === 1 ? "" : "s" }), r.schoolYear ? ct("hwreceive.written_for", "written for {year}", { year: r.schoolYear }) : "", ct("hwreceive.from_another", "shared by another family")].filter(Boolean).join(" · ")
                    : ct("requests.homework_sheet_sub", "{n} word{plural} read from a photo", { n: (r.pairs || []).length, plural: (r.pairs || []).length === 1 ? "" : "s" })}</div>
                  <button onClick={() => setReviewing(r.id)} className="w-full bg-slate-900 text-white text-sm font-bold rounded-lg py-2.5" data-testid={`hw-sheet-open-${r.id}`}>{ct("requests.homework_sheet_open", "Check the words")}</button>
                </div>
              )}
              {r.image && (
                <button onClick={() => setEnlargedImage(r.image)} className="mb-2 flex-shrink-0" aria-label="View attached photo" title="View attached photo">
                  <img src={imageSrc(r.image)} alt="" className="w-14 h-14 rounded-lg object-cover" />
                </button>
              )}
              {r.requestType === "habit_reward" && (() => {
                // Habit-reward requests don't carry a single r.image the way redemption/earn
                // requests can — the photos (if any of that week's requirePhoto tasks were
                // checked off with one) live on the individual habitLog entries instead, so
                // pull them together here at render time rather than duplicating them onto
                // the request record.
                const images = state.habitLog
                  .filter((l) => l.childId === r.childId && l.image && weekStartOf(l.date) === r.weekStart)
                  .map((l) => l.image);
                if (images.length === 0) return null;
                return (
                  <div className="flex gap-1.5 mb-2 flex-wrap">
                    {images.map((src, i) => (
                      <button key={i} onClick={() => setEnlargedImage(src)} className="flex-shrink-0" aria-label="View attached photo" title="View attached photo">
                        <img src={imageSrc(src)} alt="" className="w-12 h-12 rounded-lg object-cover" />
                      </button>
                    ))}
                  </div>
                );
              })()}
              {(r.requestType === "goal_redeem" || (r.requestType === ASK_TO_BUY_TYPE && askFacts(r)?.availability.affordable)) && (() => {
                const { tokenPart, vaultPart } = computeRedeemSplit(r);
                const { toggleable, canAllVault } = requestRedeemSourceOptions(r);
                // Tokens is clickable in two distinct situations: the genuine either/or toggle
                // (both balances alone sufficient), or — new — the narrower case where tokens
                // alone fall short but the Vault alone would still cover the whole thing, which
                // lets a parent deselect tokens from the default mixed split and pay entirely
                // from the Vault instead. Savings Vault stays a plain either/or pick, same as
                // before: deselecting it only ever makes sense when tokens alone are already
                // enough, which is exactly the `toggleable` case it's already gated on.
                const tokensClickable = toggleable || canAllVault;
                function clickTokens() {
                  if (toggleable) { setPayFromOverride((o) => ({ ...o, [r.id]: "token" })); return; }
                  if (!canAllVault) return;
                  // Toggle: deselect tokens (pay from the Vault alone) if not already
                  // deselected, otherwise revert to the default mixed split.
                  setPayFromOverride((o) => ({ ...o, [r.id]: o[r.id] === "vault-only" ? "token" : "vault-only" }));
                }
                return (
                  <div className="flex items-center gap-2 mb-2 flex-wrap">
                    <span className="text-xs font-medium text-slate-500">{ct("requests.pay_from_label", "Pay from:")}</span>
                    <div className="flex rounded-lg overflow-hidden border border-slate-200">
                      <button
                        type="button"
                        disabled={!tokensClickable}
                        onClick={clickTokens}
                        className={`px-2.5 py-1 text-xs font-bold flex items-center gap-1 ${tokenPart > 0 ? "bg-slate-800 text-white" : "bg-white text-slate-300"} ${tokensClickable ? "cursor-pointer" : "cursor-default"}`}
                      ><CoinIcon className="w-3 h-3" /> {ct("requests.pay_from_tokens", "Tokens")}</button>
                      <button
                        type="button"
                        disabled={!toggleable}
                        onClick={() => setPayFromOverride((o) => ({ ...o, [r.id]: "vault" }))}
                        className={`px-2.5 py-1 text-xs font-bold flex items-center gap-1 ${vaultPart > 0 ? "bg-slate-800 text-white" : "bg-white text-slate-300"} ${toggleable ? "cursor-pointer" : "cursor-default"}`}
                      ><VaultIcon className="w-3.5 h-3.5 inline-block" /> {ct("requests.pay_from_vault", "Savings Vault")}</button>
                    </div>
                    <span className="text-xs text-slate-400">
                      {tokenPart > 0 && vaultPart > 0
                        ? `${tokenPart} tokens + ${fmtPence(tokensToPence(vaultPart, rate))}`
                        : vaultPart > 0 ? fmtPence(tokensToPence(vaultPart, rate)) : `${tokenPart} tokens`}
                      {!toggleable && !canAllVault && (tokenPart > 0 && vaultPart > 0) && " · both needed to cover this"}
                    </span>
                  </div>
                );
              })()}
              {r.breachesCap && (() => {
                const child = state.profiles.find((p) => p.id === r.childId);
                const capGBP = (child?.spendingCap?.weeklyGBP || 0).toFixed(2);
                return (
                  <div className="bg-rose-50 border border-rose-200 rounded-lg px-2.5 py-1.5 mb-2 text-xs font-semibold text-rose-700">
                    <Icon id="ic-attention" size={14} hue="amber" className="inline-block align-[-2px] mr-1" />Approving this will take {childName(r.childId)} over their £{capGBP}/week cap
                    {/* W4 cap deep-link: the cap lives on the child, so jump straight to them. */}
                    {onAdjustCap && (
                      <button className="block mt-1 underline font-bold text-rose-600" data-testid={`cap-adjust-${r.id}`} onClick={() => onAdjustCap(r.childId)}>{ct("requests.cap_adjust_link", "Adjust their cap")}</button>
                    )}
                  </div>
                );
              })()}
              {r.requestType === ASK_TO_BUY_TYPE ? (() => {
                const affordable = !!askFacts(r)?.availability.affordable;
                const picked = gapCoverChoice[r.id] === "gift" || gapCoverChoice[r.id] === "loan";
                return (
                  <div className="flex gap-2">
                    <button className="flex-1 bg-slate-900 text-white text-xs font-bold rounded-lg py-1.5 disabled:opacity-40" disabled={!affordable && !picked} onClick={() => (affordable ? approve(r) : setGapConfirmId(r.id))} data-testid="ask-approve">{affordable ? ct("requests.ask_approve", "Approve") : ct("requests.ask_cover_approve", "Cover and approve")}</button>
                    <button className="flex-1 bg-pill text-navy text-xs font-bold rounded-lg py-1.5" onClick={() => makeGoal(r)} data-testid="ask-make-goal">{ct("requests.ask_make_goal", "Make it a goal")}</button>
                    <button className="flex-1 bg-pill text-navy text-xs font-bold rounded-lg py-1.5" onClick={() => reject(r)} data-testid="ask-not-this-time">{ct("requests.ask_not_this_time", "Not this time")}</button>
                  </div>
                );
              })() : (
              <div className="flex gap-2">
                {/* LB-382 receive fixes (Alan, 1 Oct): a homework request — a sheet the child sent OR
                    a list another family shared — has ONE button, Check the words. A generic
                    Approve on a shared list resolved it with no list made for any child. */}
                {!isHomeworkRequest(r) && <button className="flex-1 bg-slate-900 text-white text-xs font-bold rounded-lg py-1.5" onClick={() => approve(r)}>{r.requestType === "vault_transfer" ? "Confirm transfer made" : "Approve"}</button>}
                {!isHomeworkRequest(r) && <button className="flex-1 bg-pill text-navy text-xs font-bold rounded-lg py-1.5" onClick={() => reject(r)}>{ct("requests.reject_button", "Reject")}</button>}
              </div>
              )}
            </div>
            );
          })}
        </div>
        {pendingTotalPages > 1 && (
          <div className="flex items-center justify-between mt-3 pt-3 border-t border-slate-100">
            <button disabled={pendingPageClamped === 0} className="text-xs font-bold text-slate-500 disabled:opacity-30" onClick={() => setPendingPage(pendingPageClamped - 1)}>← Prev</button>
            <span className="text-xs text-slate-400">Page {pendingPageClamped + 1} of {pendingTotalPages}</span>
            <button disabled={pendingPageClamped >= pendingTotalPages - 1} className="text-xs font-bold text-slate-500 disabled:opacity-30" onClick={() => setPendingPage(pendingPageClamped + 1)}>Next →</button>
          </div>
        )}
      </div>
      <CollapsibleSection
        title="Recently resolved"
        defaultOpen={false}
      >
        <ActivityList
          items={resolved}
          emptyText="Nothing yet."
          renderRow={(r) => {
            const kindStyle = ACTIVITY_KIND_STYLE[r.requestType] || ACTIVITY_KIND_STYLE.earn;
            let amount, amountColorClass;
            if (isHomeworkRequest(r)) {
              // A sheet or a shared list is not money: no figure, and no Undo — the list it made
              // is archived or deleted from its own page.
              amount = "";
              amountColorClass = "text-slate-300";
            } else if (r.status !== "approved") {
              // LB-17: an ask turned into a goal spent nothing either.
              amount = "—";
              amountColorClass = "text-slate-300";
            } else if (r.requestType === "vault_transfer") {
              amount = `${r.requestedAmount} → ${fmtPence(r.pence || tokensToPence(r.requestedAmount, rate))}`;
              amountColorClass = "text-teal-600";
            } else {
              const spend = r.requestType === "goal_redeem" || r.requestType === "experience_redeem" || r.requestType === ASK_TO_BUY_TYPE;
              amount = `${spend ? "−" : "+"}${r.requestedAmount}`;
              amountColorClass = spend ? "text-amber-600" : "text-emerald-500";
            }
            return (
              <ActivityRow
                key={r.id}
                icon={kindStyle.icon}
                iconBgClass={kindStyle.iconBgClass}
                iconColorClass={kindStyle.iconColorClass}
                label={<>{childName(r.childId)} — {r.title}</>}
                timestamp={fmtActivityStamp(r.resolvedAt)}
                amount={amount}
                amountColorClass={amountColorClass}
                image={r.image}
                status={r.status}
                reversed={r.reversed}
                onUndo={reverseRequest && r.status === "approved" && !r.reversed && !isHomeworkRequest(r) ? () => reverseRequest(r.id) : undefined}
                onRemoveImage={r.image ? () => update((s) => { const x = s.requests.find((q) => q.id === r.id); if (!x || !x.image) return null; x.image = null; return s; }) : undefined}
              />
            );
          }}
        />
      </CollapsibleSection>
      <CollapsibleSection
        title="Savings Vault transactions"
        defaultOpen={false}
        summary={<p className="text-xs text-slate-500">{ct("requests.vault_tx_hint", "Match these to your kid's bank account, if you have one.")}</p>}
      >
        <ActivityList
          items={vaultTxs}
          pageSize={10}
          emptyText="Nothing yet."
          renderRow={(t) => {
            const toVault = t.vaultTransfer === "to";
            // A still-pending or already-rejected transfer is resolved through the
            // approve/reject flow above (or the child's own cancel), not this row's manual
            // undo — only a fully "approved" (or pre-existing, status-less) row can be undone
            // here.
            const undoable = !t.status || t.status === "approved";
            return (
              <ActivityRow
                key={t.id}
                icon="vault"
                iconBgClass="bg-teal-50"
                iconColorClass="text-teal-600"
                label={`${childName(t.childId)} — moved ${t.tokens} tokens ${toVault ? "to" : "from"} Savings Vault`}
                timestamp={fmtActivityStamp(t.timestamp)}
                amount={`${toVault ? "+" : "−"}${fmtPence(t.pence)}`}
                amountColorClass={toVault ? "text-emerald-500" : "text-amber-600"}
                status={t.status}
                reversed={t.reversed}
                isReversal={!!t.reversalOf}
                onUndo={reverseTransaction && undoable && !t.reversed && !t.reversalOf ? () => reverseTransaction(t.id) : undefined}
              />
            );
          }}
        />
      </CollapsibleSection>
      {gapConfirmId && (() => {
        const r = pending.find((x) => x.id === gapConfirmId);
        const facts = r && askFacts(r);
        const cover = r && gapCoverChoice[r.id];
        if (!facts || facts.availability.affordable || (cover !== "gift" && cover !== "loan")) return null;
        const short = facts.availability.shortfall;
        const vars = { short, pence: fmtPence(tokensToPence(short, rate)), name: facts.child.name, what: r.title };
        const title = cover === "gift"
          ? ct("requests.ask_confirm_gift_title", "Cover {short} tokens as a gift?", vars)
          : ct("requests.ask_confirm_loan_title", "Cover {short} tokens as a loan?", vars);
        const body = cover === "gift"
          ? ct("requests.ask_confirm_gift_body", "{name} gets {short} tokens ({pence}) as a gift, then {what} is paid for. Nothing is owed.", vars)
          : ct("requests.ask_confirm_loan_body", "{what} is paid for now and {name}\u2019s balance goes to \u2212{short}. What they earn pays it back first.", vars);
        return (
          // Backdrop is NOT a dismiss target (Alan, 28 Aug): this moves real balance.
          <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
            <div role="dialog" aria-modal="true" aria-label={title} data-testid="ask-gap-confirm" data-cover={cover} className="bg-white rounded-2xl p-5 w-full max-w-xs shadow-2xl">
              <div className="text-sm font-semibold text-slate-800 mb-1">{title}</div>
              <p className="text-xs text-slate-500 mb-4">{body}</p>
              <div className="flex gap-2">
                <button className="flex-1 bg-slate-100 text-slate-500 font-bold text-sm rounded-xl py-2" onClick={() => setGapConfirmId(null)} data-testid="ask-gap-confirm-cancel">{ct("common.cancel", "Cancel")}</button>
                <button className="flex-1 bg-slate-900 text-white font-bold text-sm rounded-xl py-2" onClick={() => { setGapConfirmId(null); approve(r); }} data-testid="ask-gap-confirm-yes">{cover === "gift" ? ct("requests.ask_confirm_gift_cta", "Cover as a gift") : ct("requests.ask_confirm_loan_cta", "Cover as a loan")}</button>
              </div>
            </div>
          </div>
        );
      })()}
      {vaultFlow && <VaultDrainModal flow={vaultFlow} onAdvance={() => setVaultFlow((f) => f && { ...f, phase: "reminder" })} onDone={() => setVaultFlow(null)} />}
      {enlargedImage && <ImageLightbox src={enlargedImage} onClose={() => setEnlargedImage(null)} />}
    </div>
  );
}

// How a waiting request is described: who or what it is, its amount, and when it was asked.
// Exported so the waiting-requests overlay (RequestsOverlay, 2 Oct) reads a request in exactly
// these words; the tab and the overlay must never describe one differently. They sit after
// RequestsTab so Content Studio attributes their keys to the Requests screen.
export function requestWhoLabel(r, nameOf) {
  if (r.requestType === HW_SHARED_TYPE) return ct("requests.homework_shared_from", "Shared with you");
  if (r.requestType === ASK_TO_BUY_TYPE) return ct("requests.ask_to_buy_from", "{name} \u00B7 wants to buy", { name: nameOf(r.childId) });
  return nameOf(r.childId);
}
// The amount as the card shows it; "" for homework, which has none. A double-token-day earn
// shows this same "+N tokens", with the struck-through base and the chip drawn beside it.
export function requestAmountLabel(r, rate) {
  if (isHomeworkRequest(r)) return "";
  if (r.requestType === "vault_transfer") return `${r.requestedAmount} tokens → ${fmtPence(r.pence || tokensToPence(r.requestedAmount, rate))}`;
  return `${r.requestType === "goal_redeem" || r.requestType === "experience_redeem" || r.requestType === ASK_TO_BUY_TYPE ? "−" : "+"}${r.requestedAmount} tokens`;
}
export function requestWhenLabel(r) {
  return `Requested ${fmtTimestamp(r.timestamp)}`;
}

// Focus-window sequence shown after resolving something that moves real money in or out of
// the Savings Vault — approving a goal that draws on it (direction "out"), or confirming a
// child's transfer into it (direction "in", default "out" for any older caller that doesn't
// pass one): a brief animation, then a plain reminder that the vault balance is just a number
// in this app — moving the equivalent real money in the child's actual bank account is still
// on the parent. "Got it!" is the only way out, so it can't be missed and dismissed with a
// stray tap the way a toast could be.

export function VaultDrainModal({ flow, onAdvance, onDone }) {
  const direction = flow.direction || "out";
  useEffect(() => {
    if (flow.phase !== "anim") return;
    const t = setTimeout(onAdvance, 3000);
    return () => clearTimeout(t);
  }, [flow.phase]);

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl p-6 w-full max-w-xs shadow-2xl text-center">
        {flow.phase === "anim" ? (
          <>
            <VaultCoinSparkAnimation />
            <p className="text-sm font-semibold text-slate-700">Moving {fmtPence(flow.pence)} {direction === "in" ? "into" : "out of"} the Savings Vault…</p>
          </>
        ) : (
          <>
            <div className="flex items-center justify-center gap-2 mb-2">
              <CoinIcon className="w-10 h-10" />
              <span className="text-2xl text-slate-300">→</span>
              <VaultIcon className="w-10 h-10" />
            </div>
            <p className="text-sm font-semibold text-slate-800 mb-1">Don't forget the real thing.</p>
            <p className="text-sm text-slate-500 mb-4">
              This just updated {flow.childName}'s Savings Vault in the app. Remember to move <span className="font-bold text-slate-700">{fmtPence(flow.pence)}</span> {direction === "in" ? "into" : "out of"} {flow.childName}'s real bank account too, if they have one.
            </p>
            <button className="w-full bg-slate-900 text-white font-bold text-sm rounded-xl py-2.5" onClick={onDone}>{ct("requests.got_it", "Got it")}</button>
          </>
        )}
      </div>
    </div>
  );
}

/* ----------------------------- Profiles tab ------------------------------- */
